import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { LogOut, School as SchoolIcon, Users, BookOpen, ClipboardList } from 'lucide-react';

import TeachersTab from '@/pages/schoolAdmin/TeachersTab';
import ClassesTab from '@/pages/schoolAdmin/ClassesTab';
import AssignmentsTab from '@/pages/schoolAdmin/AssignmentsTab';

/**
 * School Admin dashboard — Ticket 1A.5.
 * Tabs: Teachers · Classes · Assignments.
 * Branding/colour is platform-admin-controlled (view-only here).
 */
export default function SchoolAdminHome() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [school, setSchool] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('teachers');

  const loadAll = async () => {
    try {
      const meRes = await api.get('/auth/me');
      setMe(meRes.data);
      if (meRes.data?.school_id) {
        const [schoolRes, subjectsRes] = await Promise.all([
          api.get(`/schools/${meRes.data.school_id}`),
          api.get('/subjects'),
        ]);
        setSchool(schoolRes.data);
        setSubjects(subjectsRes.data || []);
      }
    } catch {
      /* ignore — route guard handles auth */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const handleLogout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    localStorage.removeItem('yukti_session_token');
    // Land back on the school's branded login, not the Yukti marketing landing
    const slug = school?.slug;
    navigate(slug ? `/school/${slug}/login` : '/', { replace: true });
  };

  const brandColor = school?.brand_color || '#4F46E5';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50"
      data-testid="school-admin-home"
    >
      <header
        className="border-b bg-white/90 backdrop-blur sticky top-0 z-10"
        style={{ borderTopWidth: 4, borderTopColor: brandColor }}
      >
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {school?.logo_url ? (
              <img
                src={school.logo_url}
                alt={school.name}
                className="w-10 h-10 rounded-lg object-contain bg-white border"
                data-testid="school-admin-logo"
              />
            ) : (
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                style={{ backgroundColor: brandColor }}
              >
                <SchoolIcon size={20} />
              </div>
            )}
            <div>
              <h1
                className="font-bold text-slate-900"
                data-testid="school-admin-school-name"
              >
                {school?.name || 'Your School'}
              </h1>
              <p className="text-xs text-slate-500">{me?.email} · School Admin</p>
            </div>
          </div>
          <Button
            variant="ghost" size="sm"
            onClick={handleLogout}
            data-testid="school-admin-logout-btn"
          >
            <LogOut size={16} className="mr-1.5" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList
            className="mb-6 bg-white border"
            data-testid="school-admin-tabs"
          >
            <TabsTrigger value="teachers" data-testid="tab-teachers">
              <Users size={14} className="mr-1.5" /> Teachers
            </TabsTrigger>
            <TabsTrigger value="classes" data-testid="tab-classes">
              <BookOpen size={14} className="mr-1.5" /> Classes
            </TabsTrigger>
            <TabsTrigger value="assignments" data-testid="tab-assignments">
              <ClipboardList size={14} className="mr-1.5" /> Assignments
            </TabsTrigger>
          </TabsList>

          <TabsContent value="teachers">
            <TeachersTab
              school={school}
              subjects={subjects}
              brandColor={brandColor}
            />
          </TabsContent>
          <TabsContent value="classes">
            <ClassesTab school={school} brandColor={brandColor} />
          </TabsContent>
          <TabsContent value="assignments">
            <AssignmentsTab
              school={school}
              subjects={subjects}
              brandColor={brandColor}
            />
          </TabsContent>
        </Tabs>

        <p className="text-xs text-slate-400 mt-10">
          Need changes to school branding or details? Contact
          <a
            href="mailto:support@yuktilearning.com"
            className="ml-1 text-indigo-600 underline"
          >
            support@yuktilearning.com
          </a>
          .
        </p>
      </main>
    </div>
  );
}
